import { RoundingRuleRange } from '@retail/rounding/types';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { dataGridCellNoFocusOutlineClass } from '@shared/styles';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { formatPrice } from '@retail/products/utils';
import { useAppTFunction } from '@retail/app/i18n';

const baseColumnProps: Partial<GridColDef<RoundingRuleRange>> = {
  align: 'left',
  headerAlign: 'left',
  headerName: '',
  disableColumnMenu: true,
  sortable: false,
};

interface Props {
  ruleRanges: RoundingRuleRange[];
}

export const PriceRoundingConfigTable: React.FC<Props> = ({ ruleRanges }) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const columns: GridColDef<RoundingRuleRange>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'priceRange',
        headerName: t('rounding.priceRange'),
        minWidth: 150,
        flex: 0.5,
        renderCell: ({ row }) => {
          const rangeString = `${formatPrice(row.priceFrom, i18n.language)}${
            row.priceTo ? ` - ${formatPrice(row.priceTo, i18n.language)}` : '+'
          }`;
          return <Typography variant="body1">{rangeString}</Typography>;
        },
      },
      {
        ...baseColumnProps,
        field: 'roundingMode',
        minWidth: 120,
        flex: 0.5,
        headerName: t('rounding.roundingMode'),
        renderCell: ({ row }) => {
          return <Typography variant="body1">{row.getPriceRuleString(t)}</Typography>;
        },
      },
      {
        ...baseColumnProps,
        field: 'targetEndings',
        minWidth: 150,
        flex: 1,
        headerName: t('rounding.targetEndings.title'),
        renderCell: ({ row }) => {
          return (
            <Stack gap={1} direction="row" flexWrap="wrap">
              {row.targetEndings?.map((ending) => (
                <Typography
                  variant="body1"
                  key={ending}
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    bgcolor: (theme) =>
                      theme.vars
                        ? `rgba(${theme.vars.palette.secondary.lightChannel} / 0.3)`
                        : alpha(theme.palette.secondary.light, 0.3),
                    borderRadius: 1,
                  }}
                >
                  {formatPrice(ending, i18n.language)}
                </Typography>
              )) ?? '-'}
            </Stack>
          );
        },
      },
    ],
    [i18n.language, t]
  );

  return (
    <Box sx={{ width: '100%', display: 'grid' }}>
      <DataGridPremium
        getRowId={(row) => `${row.priceFrom}-${row.priceTo}`}
        rows={ruleRanges}
        columns={columns}
        getCellClassName={() => clsx(dataGridCellNoFocusOutlineClass)}
        disableRowSelectionOnClick
        disableColumnResize
        disableColumnReorder
        hideFooter
        sx={{
          bgcolor: 'inherit',
          '&': {
            '& .MuiDataGrid-columnHeader, .MuiDataGrid-columnHeaders, .MuiDataGrid-topContainer': {
              pl: 0,
              bgcolor: 'inherit',
            },
            '& .MuiDataGrid-columnHeader': {
              pl: 2,
            },
          },
          '& .MuiDataGrid-row, .MuiDataGrid-topContainer, .MuiDataGrid-row.MuiDataGrid-row--lastVisible':
            {
              borderBottom: '1px solid',
              borderBottomColor: 'divider',
            },
          '& .MuiDataGrid-filler': {
            display: 'none',
          },
        }}
        localeText={getDataGridLocalization(i18n.language as MgPrisLocale)}
      />
    </Box>
  );
};
